import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", path: "/" },
  { label: "电影", path: "/category/movie" },
  { label: "电视剧", path: "/category/tv" },
  { label: "短视频", path: "/category/short" },
  { label: "关于我们", path: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`);
    setMenuOpen(false);
    setSearchOpen(false);
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen ? "bg-background-50/95 backdrop-blur border-b border-background-200/80" : "bg-background-50/85 backdrop-blur border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer" aria-label="69视频首页">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-lg">
              <i className="ri-play-fill" aria-hidden="true"></i>
            </span>
            <span className="font-heading font-bold text-xl text-foreground-950 tracking-tight">
              69<span className="text-accent-500">视频</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.path)
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center gap-2 bg-background-100 border border-background-200/80 rounded-md px-3 py-2 w-64 focus-within:border-primary-400 transition-colors"
              role="search"
            >
              <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、电视剧、短视频"
                className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
                aria-label="搜索视频"
              />
              <button type="submit" className="text-primary-600 text-sm font-medium whitespace-nowrap cursor-pointer">
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="打开搜索"
            >
              <i className="ri-search-line text-xl" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="打开菜单"
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <form
            onSubmit={submitSearch}
            className="md:hidden flex items-center gap-2 bg-background-100 border border-background-200/80 rounded-md px-3 py-2 mb-3"
            role="search"
          >
            <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、短视频"
              className="bg-transparent outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
              aria-label="搜索视频"
            />
            <button type="submit" className="text-primary-600 text-sm font-medium whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </form>
        )}
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/80 bg-background-50 px-4 pb-4">
          <nav className="flex flex-col py-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                  isActive(item.path) ? "text-primary-600 bg-primary-50" : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}